import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import { profileData } from '@/mocks/profile';
import { works } from '@/mocks/works';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '佐佐木明希官方资料与作品全览',
  url: 'https://akiupdate.github.io',
  description: '佐佐木明希完整资料页，涵盖个人档案、出道经历、代表作品、最新动态及粉丝必知信息。',
  inLanguage: 'zh-CN',
};

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '佐佐木明希粉丝站',
  url: 'https://akiupdate.github.io',
  description: '佐佐木明希官方粉丝资料站，提供艺人档案、作品全览与最新动态。',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const featuredWorks = works.filter((w) => w.category === 'featured').slice(0, 4);
  const recentWorks = works.slice(0, 6);
  const recentArticles = articles.slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <SEOHead
        title="佐佐木明希 - 官方资料与作品全览 | 个人档案、代表作品、最新动态一站式粉丝站"
        description="佐佐木明希完整资料页，涵盖佐佐木明希的个人档案、出道经历、代表作品、最新动态及粉丝必知信息。一站式掌握佐佐木明希的全部精彩内容，持续更新，绝不错过任何动态。"
        keywords="佐佐木明希,佐佐木明希作品,佐佐木明希资料,佐佐木明希最新动态,佐佐木明希个人档案,佐佐木明希出道经历,佐佐木明希粉丝站"
        canonicalUrl="https://akiupdate.github.io"
      />

      {/* Hero Section */}
      <section className="relative w-full h-[600px] md:h-[700px] overflow-hidden" aria-label="首页主视觉">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Elegant%20artistic%20abstract%20composition%20with%20soft%20warm%20rose%20and%20golden%20tones%2C%20flowing%20fabric%20textures%2C%20delicate%20cherry%20blossom%20petals%20floating%20in%20gentle%20light%2C%20dreamy%20atmospheric%20gradient%20background%2C%20minimalist%20Japanese%20aesthetic%2C%20soft%20focus%2C%20elegant%20and%20feminine%20mood%2C%20editorial%20quality%2C%20high%20detail%2C%20luxurious%20texture&width=1920&height=1080&seq=sasaki-hero-2025&orientation=landscape)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full max-w-3xl mx-auto">
            <p className="text-white/70 text-sm md:text-base font-label tracking-widest mb-4 opacity-0 animate-[fadeIn_0.6s_ease_forwards]">
              OFFICIAL FAN CLUB
            </p>
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight opacity-0 animate-[fadeIn_0.6s_ease_0.1s_forwards]">
              佐佐木明希
            </h2>
            <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed opacity-0 animate-[fadeIn_0.6s_ease_0.2s_forwards]">
              个人档案 · 代表作品 · 最新动态 · 粉丝必知<br />
              一站式掌握佐佐木明希的全部精彩内容
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 opacity-0 animate-[fadeIn_0.6s_ease_0.3s_forwards]">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览全部作品
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full text-sm font-label font-medium hover:bg-white/25 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-white/50 text-2xl"></i>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-10 px-4 md:px-6" aria-label="数据统计">
        <div className="max-w-4xl mx-auto bg-background-50 rounded-2xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: `${profileData.stats.totalWorks}+`, label: '作品总数' },
            { value: `${profileData.stats.awards}项`, label: '荣誉奖项' },
            { value: `${profileData.stats.yearsActive}年`, label: '演艺生涯' },
            { value: `${(profileData.stats.fanCount / 10000).toFixed(0)}万+`, label: '粉丝关注' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                {stat.value}
              </div>
              <div className="text-foreground-500 text-sm font-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Profile Overview */}
      <section className="py-16 md:py-20 px-4 md:px-6" aria-label="个人档案概览">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="lg:w-2/5 w-full">
              <div className="relative overflow-hidden rounded-2xl w-full aspect-[3/4] max-w-sm mx-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20woman%20silhouette%20in%20soft%20warm%20lighting%2C%20artistic%20portrait%20photography%20style%2C%20flowing%20dark%20hair%2C%20wearing%20simple%20white%20dress%2C%20dreamy%20bokeh%20background%20with%20cherry%20blossom%20hints%2C%20soft%20focus%2C%20minimalist%20composition%2C%20editorial%20quality%2C%20feminine%20and%20graceful%20mood%2C%20warm%20rose%20and%20cream%20tones&width=800&height=1067&seq=sasaki-portrait-main&orientation=portrait"
                  alt="佐佐木明希个人形象照"
                  title="佐佐木明希 个人档案"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:w-3/5">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium mb-4">
                个人档案
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                关于 {profileData.name}
              </h3>
              <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
                {profileData.bio}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: '出生日期', value: profileData.birthDate },
                  { label: '出生地', value: profileData.birthPlace },
                  { label: '身高', value: profileData.height },
                  { label: '出道时间', value: profileData.debutDate },
                  { label: '经纪公司', value: profileData.agency },
                  { label: '特长', value: profileData.specialty },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className="text-foreground-400 text-xs font-label whitespace-nowrap">{item.label}</span>
                    <span className="text-foreground-900 text-sm font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-primary-500 text-sm font-label font-medium hover:text-primary-600 transition-colors cursor-pointer"
              >
                查看完整档案 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-16 md:py-20 bg-background-100 px-4 md:px-6" aria-label="精选推荐作品">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium mb-3">
                精选推荐
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                代表作品
              </h3>
            </div>
            <Link
              to="/category/featured"
              className="hidden sm:flex items-center gap-2 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredWorks.map((work) => (
              <Link
                key={work.id}
                to={`/article/${work.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={work.coverImage}
                    alt={work.title}
                    title={`佐佐木明希作品 - ${work.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                    {work.title}
                  </h4>
                  <div className="flex items-center gap-3 text-xs text-foreground-500 font-label">
                    <span>{work.releaseDate}</span>
                    <span>·</span>
                    <span>{work.duration}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/category/featured"
            className="sm:hidden flex items-center justify-center gap-2 mt-6 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer"
          >
            查看全部精选 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Latest Works */}
      <section className="py-16 md:py-20 px-4 md:px-6" aria-label="最新作品">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium mb-3">
                最新发布
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                最新作品
              </h3>
            </div>
            <Link
              to="/category/all"
              className="hidden sm:flex items-center gap-2 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部作品 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recentWorks.map((work) => (
              <Link
                key={work.id}
                to={`/article/${work.id}`}
                className="group flex gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
              >
                <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                  <img
                    src={work.thumbImage}
                    alt={work.title}
                    title={`佐佐木明希作品 - ${work.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2 mb-1.5">
                    {work.title}
                  </h4>
                  <p className="text-xs text-foreground-500 font-label mb-2">{work.publisher}</p>
                  <div className="flex items-center gap-2 text-xs text-foreground-400 font-label">
                    <span>{work.releaseDate}</span>
                    <span>{work.duration}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/category/all"
            className="sm:hidden flex items-center justify-center gap-2 mt-6 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer"
          >
            查看全部作品 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-16 md:py-20 bg-background-100 px-4 md:px-6" aria-label="最新动态">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium mb-3">
                最新动态
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                资讯与新闻
              </h3>
            </div>
            <Link
              to="/category/news"
              className="hidden sm:flex items-center gap-2 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              更多动态 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {recentArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`佐佐木明希动态 - ${article.title}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-800 text-xs font-label backdrop-blur-sm">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs text-foreground-400 font-label mb-2">{article.publishDate}</div>
                  <h4 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h4>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                    {article.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/category/news"
            className="sm:hidden flex items-center justify-center gap-2 mt-6 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer"
          >
            查看全部动态 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 md:px-6" aria-label="常见问题">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium mb-3">
              粉丝必知
            </span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              常见问题
            </h3>
          </div>
          <div className="space-y-3">
            {faqItems.map((item) => (
              <div
                key={item.id}
                className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                  aria-expanded={openFaq === item.id}
                >
                  <span className="font-heading font-semibold text-foreground-900 text-sm pr-4">
                    {item.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 text-lg flex-shrink-0 ${
                      openFaq === item.id ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === item.id && (
                  <div className="px-5 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-20 md:py-24 overflow-hidden"
        aria-label="关注我们"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Soft%20abstract%20gradient%20background%20with%20warm%20rose%20and%20cream%20tones%2C%20flowing%20silk%20textures%2C%20minimalist%20Japanese%20aesthetic%2C%20elegant%20atmospheric%20mood%2C%20soft%20light%20particles%2C%20editorial%20quality%2C%20luxurious%20texture%2C%20dreamy%20and%20feminine&width=1920&height=600&seq=sasaki-cta-2025&orientation=landscape)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/60 to-background-950/40" />
        <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            不错过佐佐木明希的任何动态
          </h3>
          <p className="text-white/70 text-sm mb-8">
            持续更新佐佐木明希的最新作品、活动和独家内容，加入我们共同关注。
          </p>
          <Link
            to="/category/news"
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 text-white rounded-full text-sm font-label font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-notification-3-line"></i>
            查看最新动态
          </Link>
        </div>
      </section>
    </>
  );
}